/**
 * Import Monitor — Orphan Shadow tab.
 *
 * Orders the ERP has stopped sending, in two groups that mean different things:
 *
 *   Went missing — carried by a push at some point, then absent from N
 *   consecutive COMPLETE pushes. The automatic sweep acts on these when the
 *   mode is switched on.
 *
 *   Never seen — carried by none of the complete pushes since stamping began.
 *   Also dropped, but held back from the sweep because it cannot tell a
 *   dropped order from one that predates the feed or arrived by another route.
 *
 * Both groups are selectable and cancellable by hand. Every cancellation,
 * swept or manual, writes an audit row. The report itself never writes — it
 * reads rows the hourly sweep recorded.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, RefreshCw, ShieldAlert, EyeOff, CheckCircle2, PackageX, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  importsAPI,
  type OrphanCandidate,
  type OrphanShadowReport,
  type OrphanSiteReport,
} from '../../lib/api/imports';

const MODE_STYLES: Record<string, string> = {
  on: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 border-red-200 dark:border-red-800',
  shadow: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  off: 'bg-muted text-muted-foreground border-border',
};

/** Older than this and it is very unlikely to come back. */
const STALE_DAYS = 21;

function Stat({ label, value, tone = 'default' }: {
  label: string; value: React.ReactNode; tone?: 'default' | 'warn' | 'danger';
}) {
  const toneClass =
    tone === 'danger' ? 'text-red-600 dark:text-red-400'
      : tone === 'warn' ? 'text-amber-600 dark:text-amber-400'
        : 'text-foreground';
  return (
    <div className="rounded-lg border bg-background px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

interface TableProps {
  rows: OrphanCandidate[];
  showStamp: boolean;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[], select: boolean) => void;
}

function CandidateTable({ rows, showStamp, selected, onToggle, onToggleAll }: TableProps) {
  if (rows.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border px-3 py-4 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nothing here.
      </div>
    );
  }
  const allIds = rows.map(r => r.line_id);
  const allSelected = allIds.every(id => selected.has(id));
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-xs">
        <thead className="bg-muted/60 text-muted-foreground">
          <tr>
            <th className="py-2 pl-3 pr-2 w-8">
              <input
                type="checkbox" aria-label="Select all"
                checked={allSelected}
                onChange={() => onToggleAll(allIds, !allSelected)}
              />
            </th>
            <th className="text-left py-2 pr-3 font-medium">Order</th>
            <th className="text-left py-2 pr-3 font-medium">Ln</th>
            <th className="text-left py-2 pr-3 font-medium">Part</th>
            <th className="text-right py-2 pr-3 font-medium">Qty</th>
            <th className="text-right py-2 pr-3 font-medium">Recvd</th>
            <th className="text-right py-2 pr-3 font-medium">Age</th>
            {showStamp && <th className="text-right py-2 pr-3 font-medium">Last push</th>}
            <th className="text-left py-2 pr-3 font-medium">Customer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const isSel = selected.has(r.line_id);
            const stale = (r.age_days ?? 0) >= STALE_DAYS;
            return (
              <tr
                key={r.line_id}
                onClick={() => onToggle(r.line_id)}
                className={`border-t cursor-pointer transition-colors ${
                  isSel ? 'bg-primary/10' : 'hover:bg-muted/40'
                }`}
              >
                <td className="py-1.5 pl-3 pr-2" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${r.po_number} line ${r.line_number}`}
                    checked={isSel}
                    onChange={() => onToggle(r.line_id)}
                  />
                </td>
                <td className="py-1.5 pr-3 font-mono font-medium">{r.po_number}</td>
                <td className="py-1.5 pr-3 tabular-nums">{r.line_number}</td>
                <td className="py-1.5 pr-3 font-mono text-muted-foreground">{r.part_number || '—'}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.quantity}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums">{r.received_qty}</td>
                <td className={`py-1.5 pr-3 text-right tabular-nums ${
                  stale ? 'text-amber-600 dark:text-amber-400 font-medium' : ''
                }`}>
                  {r.age_days != null ? `${r.age_days}d` : '—'}
                </td>
                {showStamp && (
                  <td className="py-1.5 pr-3 text-right font-mono text-muted-foreground">
                    {r.last_seen_import_no ?? '—'}
                  </td>
                )}
                <td className="py-1.5 pr-3 truncate max-w-[16rem]">{r.customer}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface GroupProps {
  title: string;
  blurb: string;
  icon: React.ReactNode;
  rows: OrphanCandidate[];
  showStamp: boolean;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[], select: boolean) => void;
  onCancel: (rows: OrphanCandidate[]) => Promise<void>;
  cancelling: boolean;
}

function Group({
  title, blurb, icon, rows, showStamp, selected, onToggle, onToggleAll, onCancel, cancelling,
}: GroupProps) {
  const chosen = rows.filter(r => selected.has(r.line_id));
  return (
    <section className="space-y-2">
      <div className="flex items-center gap-2">
        {icon}
        <h4 className="text-sm font-semibold">{title}</h4>
        <span className="text-xs font-medium text-muted-foreground tabular-nums">{rows.length}</span>
      </div>
      <p className="text-xs text-muted-foreground max-w-3xl">{blurb}</p>
      <CandidateTable
        rows={rows} showStamp={showStamp}
        selected={selected} onToggle={onToggle} onToggleAll={onToggleAll}
      />
      {chosen.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap rounded-lg border bg-muted/40 px-3 py-2">
          <span className="text-xs font-medium tabular-nums">{chosen.length} selected</span>
          <button
            onClick={() => void onCancel(chosen)}
            disabled={cancelling}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium
                       rounded-lg bg-destructive text-destructive-foreground disabled:opacity-50
                       hover:opacity-90 transition-opacity"
          >
            {cancelling ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackageX className="h-3 w-3" />}
            Cancel selected
          </button>
          <span className="text-xs text-muted-foreground">
            Marks them cancelled, zeroes the outstanding quantity, closes any order
            left with no open lines, and records it against you.
          </span>
        </div>
      )}
    </section>
  );
}

interface SiteBlockProps {
  site: OrphanSiteReport;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[], select: boolean) => void;
  onCancel: (rows: OrphanCandidate[], siteName: string) => Promise<void>;
  cancelling: boolean;
}

function SiteBlock({ site, selected, onToggle, onToggleAll, onCancel, cancelling }: SiteBlockProps) {
  const total = site.would_cancel_count + site.never_seen_count;
  const pct = site.open_lines ? (total / site.open_lines) * 100 : 0;
  const cancelFor = useCallback(
    (rows: OrphanCandidate[]) => onCancel(rows, site.site_name),
    [onCancel, site.site_name],
  );

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between gap-3 flex-wrap border-b px-4 py-3">
        <h3 className="text-sm font-semibold">{site.site_name}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Open lines" value={site.open_lines} />
          <Stat label="Stamped" value={site.stamped_lines} />
          <Stat label="Went missing" value={site.would_cancel_count}
                tone={site.would_cancel_count ? 'danger' : 'default'} />
          <Stat label="Never seen" value={site.never_seen_count}
                tone={site.never_seen_count ? 'warn' : 'default'} />
        </div>
      </div>

      {site.skipped_reason && (
        <div className="mx-4 mt-3 rounded-lg border border-amber-300 bg-amber-50
                        dark:bg-amber-950/30 px-3 py-2 text-xs">
          <strong>Sweep abstained.</strong> {site.skipped_reason}
        </div>
      )}

      {total > 0 && pct >= 5 && !site.skipped_reason && (
        <div className="mx-4 mt-3 rounded-lg border px-3 py-2 text-xs text-muted-foreground">
          {total} of {site.open_lines} open lines ({pct.toFixed(1)}%) look dropped.
        </div>
      )}

      <div className="p-4 space-y-6">
        <Group
          title="Went missing" showStamp
          icon={site.would_cancel_count > 0
            ? <ShieldAlert className="h-4 w-4 text-red-600" />
            : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          blurb="Carried by a push at some point, then absent from the last consecutive
                 complete pushes. The automatic sweep cancels these once the mode is
                 switched on — or you can cancel them here now."
          rows={site.would_cancel}
          selected={selected} onToggle={onToggle} onToggleAll={onToggleAll}
          onCancel={cancelFor} cancelling={cancelling}
        />
        <Group
          title="Never seen in any push" showStamp={false}
          icon={<EyeOff className="h-4 w-4 text-amber-600" />}
          blurb="Carried by none of the complete pushes since stamping began — they have
                 dropped. Held back from the automatic sweep because it cannot tell a
                 dropped order from one that predates the feed or came in by another
                 route, so confirm and cancel them here."
          rows={site.never_seen}
          selected={selected} onToggle={onToggle} onToggleAll={onToggleAll}
          onCancel={cancelFor} cancelling={cancelling}
        />
      </div>
    </div>
  );
}

interface Props {
  siteId?: number | null;
}

export default function ImportOrphanShadowTab({ siteId }: Props) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<OrphanShadowReport | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await importsAPI.getOrphanShadow(siteId ? { site: siteId } : undefined));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not load the report');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { void load(); }, [load]);

  const toggle = useCallback((id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: number[], select: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      ids.forEach(id => { if (select) next.add(id); else next.delete(id); });
      return next;
    });
  }, []);

  const cancelSelected = useCallback(async (rows: OrphanCandidate[], siteName: string) => {
    const ids = rows.map(r => r.line_id);
    if (ids.length === 0) return;
    const preview = rows.slice(0, 5).map(r => `  ${r.po_number} line ${r.line_number}`).join('\n');
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(
      `Cancel ${ids.length} line(s) on ${siteName}?\n\n${preview}` +
      (rows.length > 5 ? `\n  …and ${rows.length - 5} more` : '') +
      '\n\nThey will be marked cancelled, their outstanding quantity zeroed, and any ' +
      'order left with no open lines closed. Recorded against your user.',
    )) return;
    setCancelling(true);
    try {
      const res = await importsAPI.cancelOrphans(ids);
      toast.success(
        `Cancelled ${res.cancelled} line(s)` +
        (res.closed_headers ? `, closed ${res.closed_headers} order(s)` : ''),
      );
      setSelected(new Set());
      await load();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  }, [load]);

  const totals = useMemo(() => {
    const s = report?.sites || [];
    return {
      missing: s.reduce((n, x) => n + x.would_cancel_count, 0),
      never: s.reduce((n, x) => n + x.never_seen_count, 0),
    };
  }, [report]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading orphan report…
      </div>
    );
  }
  if (!report) return null;

  const candidates = totals.missing + totals.never;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold">Orders the ERP has stopped sending</h3>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${
              MODE_STYLES[report.mode] || MODE_STYLES['shadow']
            }`}>
              {report.mode === 'on' ? 'cancelling automatically' : `sweep: ${report.mode}`}
            </span>
          </div>
          <p className="text-xs text-muted-foreground max-w-3xl">
            {candidates} candidate{candidates === 1 ? '' : 's'} · threshold{' '}
            {report.missed_pushes} consecutive complete pushes ·{' '}
            {siteId ? 'scoped to the site selected in the header' : 'all sites'}
          </p>
          {report.last_sweep_at && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              last sweep {new Date(report.last_sweep_at).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border rounded-lg
                     hover:bg-muted transition-colors"
        >
          <RefreshCw className="h-3 w-3" /> Reload
        </button>
      </div>

      {report.sites.map(s => (
        <SiteBlock
          key={s.site_id} site={s}
          selected={selected} onToggle={toggle} onToggleAll={toggleAll}
          onCancel={cancelSelected} cancelling={cancelling}
        />
      ))}
    </div>
  );
}
